import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import ExportToolbar from "@/components/ExportToolbar";
import { exportExcel, exportPDF } from "@/lib/exporters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Save } from "lucide-react";
import { devError } from "@/lib/logger";

function Stat({ label, value, accent }) {
  return (
    <div className="border border-gray-200 rounded bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${accent || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const navigate = useNavigate();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;

  const [bankLedgers, setBankLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const [data, setData] = useState(null);
  // Pending UI edits: rowKey -> { reconciled, statement_date, statement_ref }
  const [edits, setEdits] = useState({});
  const [bulkDate, setBulkDate] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch bank/cash ledgers
  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    (async () => {
      const [banks, cash] = await Promise.all([
        api.get(`/companies/${activeId}/ledgers`, { params: { group: "Bank Accounts" } }),
        api.get(`/companies/${activeId}/ledgers`, { params: { group: "Cash-in-Hand" } }),
      ]);
      if (!mounted) return;
      const all = [...banks.data, ...cash.data];
      setBankLedgers(all);
      if (all.length && !ledgerId) setLedgerId(all[0].id);
    })();
    return () => { mounted = false; };
  }, [activeId]);

  // Fetch reconciliation
  useEffect(() => {
    if (!activeId || !ledgerId) return;
    let mounted = true;
    api
      .get(`/companies/${activeId}/bank/${ledgerId}/reconciliation`, { params })
      .then((r) => { if (mounted) { setData(r.data); setEdits({}); } })
      .catch((err) => {
        devError("Reconciliation fetch failed", err);
        toast.error(err.response?.data?.detail || "Failed to load reconciliation");
      });
    return () => { mounted = false; };
  }, [activeId, ledgerId, applied.start, applied.end]);

  // Merge edits onto rows for display
  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.map((r) => {
      const k = `${r.voucher_id}:${r.entry_idx}`;
      const ed = edits[k];
      return {
        ...r,
        _key: k,
        _reconciled: ed ? ed.reconciled : r.reconciled,
        _statement_date: ed ? ed.statement_date : (r.statement_date || ""),
        _statement_ref: ed ? ed.statement_ref : (r.statement_ref || ""),
      };
    });
  }, [data, edits]);

  // Live totals (taking pending edits into account)
  const totals = useMemo(() => {
    if (!data) return null;
    const opening = data.opening_balance;
    let book = opening;
    let reconciled = opening;   // running balance counting only reconciled entries
    rows.forEach((r) => {
      const signed = r.dr_cr === "Dr" ? r.amount : -r.amount;
      book += signed;
      if (r._reconciled) reconciled += signed;
    });
    const unreconciled = book - reconciled;
    return { opening, book, reconciled, unreconciled, statement: book - unreconciled };
  }, [rows, data]);

  const updateRow = (key, patch) => {
    setEdits((e) => ({ ...e, [key]: { ...(e[key] || {}), ...patch } }));
  };

  const toggleRow = (row) => {
    const newRec = !row._reconciled;
    updateRow(row._key, {
      reconciled: newRec,
      statement_date: newRec ? (row._statement_date || bulkDate || row.date) : "",
      statement_ref: newRec ? (row._statement_ref || "") : "",
    });
  };

  const allVisibleSelected = rows.length > 0 && rows.every((r) => r._reconciled);
  const someSelected = rows.some((r) => r._reconciled) && !allVisibleSelected;

  const toggleAll = () => {
    const newRec = !allVisibleSelected;
    const patch = {};
    rows.forEach((r) => {
      patch[r._key] = {
        reconciled: newRec,
        statement_date: newRec ? (r._statement_date || bulkDate || r.date) : "",
        statement_ref: r._statement_ref || "",
      };
    });
    setEdits((e) => ({ ...e, ...patch }));
  };

  const applyBulkDate = () => {
    if (!bulkDate) {
      toast.error("Pick a statement date first");
      return;
    }
    const patch = {};
    rows.forEach((r) => {
      if (r._reconciled) {
        patch[r._key] = { reconciled: true, statement_date: bulkDate, statement_ref: r._statement_ref || "" };
      }
    });
    setEdits((e) => ({ ...e, ...patch }));
    toast.success("Statement date applied to reconciled rows");
  };

  const onSave = async () => {
    if (!data) return;
    const toReconcile = [];
    const toUnreconcile = [];
    rows.forEach((r) => {
      const original = r.reconciled;
      const now = r._reconciled;
      if (now && (!original || r._statement_date !== (r.statement_date || "") || r._statement_ref !== (r.statement_ref || ""))) {
        if (!r._statement_date) {
          // skip — user toggled on but no statement date
          return;
        }
        toReconcile.push({
          voucher_id: r.voucher_id,
          entry_idx: r.entry_idx,
          statement_date: r._statement_date,
          statement_ref: r._statement_ref || "",
        });
      } else if (!now && original) {
        toUnreconcile.push(r.voucher_id);
      }
    });
    if (!toReconcile.length && !toUnreconcile.length) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      if (toReconcile.length) {
        await api.post(`/companies/${activeId}/bank/reconcile`, { ledger_id: ledgerId, items: toReconcile });
      }
      if (toUnreconcile.length) {
        await api.post(`/companies/${activeId}/bank/unreconcile`, { ledger_id: ledgerId, voucher_ids: toUnreconcile });
      }
      toast.success(`Saved: ${toReconcile.length} reconciled, ${toUnreconcile.length} cleared`);
      // refetch
      const r = await api.get(`/companies/${activeId}/bank/${ledgerId}/reconciliation`, { params });
      setData(r.data);
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onExcel = () => {
    if (!data) return;
    const sheets = [
      {
        name: "Reconciliation",
        rows: [
          ["Bank Reconciliation"],
          ["Company", active?.name],
          ["Bank", data.ledger.name],
          ["Period", formatRangeLabel(range)],
          [],
          ["Opening", data.opening_balance],
          ["Book balance", totals.book],
          ["Reconciled balance", totals.reconciled],
          ["Unreconciled", totals.unreconciled],
          ["Statement balance (computed)", totals.statement],
          [],
          ["Date", "Voucher", "Type", "Particulars", "Reference", "Dr", "Cr", "Running Bal", "Reconciled", "Statement Date", "Statement Ref"],
          ...rows.map((r) => [
            r.date, r.voucher_no, r.voucher_type, r.particulars, r.reference,
            r.dr_cr === "Dr" ? r.amount : "", r.dr_cr === "Cr" ? r.amount : "",
            r.balance, r._reconciled ? "Yes" : "No", r._statement_date || "", r._statement_ref || "",
          ]),
        ],
      },
    ];
    exportExcel(`bank-recon-${data.ledger.name}`, sheets);
  };

  const onPDF = () => {
    if (!data) return;
    const sections = [
      {
        title: "Summary",
        headers: ["", "Amount (₹)"],
        rows: [
          ["Opening", formatINR(data.opening_balance)],
          ["Book balance", formatINR(totals.book)],
          ["Reconciled balance", formatINR(totals.reconciled)],
          ["Unreconciled", formatINR(totals.unreconciled)],
          ["Statement balance", formatINR(totals.statement)],
        ],
      },
      {
        title: "Transactions",
        headers: ["Date", "Voucher", "Particulars", "Dr", "Cr", "Balance", "Reconciled", "Stmt Date"],
        rows: rows.map((r) => [
          r.date, r.voucher_no, r.particulars,
          r.dr_cr === "Dr" ? formatINR(r.amount) : "",
          r.dr_cr === "Cr" ? formatINR(r.amount) : "",
          formatINR(r.balance),
          r._reconciled ? "Y" : "N",
          r._statement_date || "",
        ]),
      },
    ];
    exportPDF(`bank-recon-${data.ledger.name}`, `Bank Reconciliation — ${data.ledger.name}`, sections, { company: active?.name });
  };

  return (
    <>
      <PageHeader
        title="Bank Reconciliation"
        subtitle={`${active?.name || ""}  •  ${data?.ledger?.name || "—"}  •  ${formatRangeLabel(range)}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/banking")} className="h-8" data-testid="btn-back-banking">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            <ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />
            <Button onClick={onSave} disabled={saving || !Object.keys(edits).length} className="h-8" data-testid="btn-save-reconciliation">
              <Save className="w-4 h-4 mr-1.5" /> Save changes {Object.keys(edits).length ? `(${Object.keys(edits).length})` : ""}
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-4" data-testid="reconciliation-page">
        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Bank / Cash Ledger</label>
            <Select value={ledgerId} onValueChange={setLedgerId}>
              <SelectTrigger className="h-9 w-60" data-testid="select-bank-ledger">
                <SelectValue placeholder="Pick a bank ledger" />
              </SelectTrigger>
              <SelectContent>
                {bankLedgers.map((L) => (
                  <SelectItem key={L.id} value={L.id} data-testid={`opt-bank-${L.id}`}>
                    {L.name} <span className="text-xs text-gray-400 ml-2">({L.group})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Bulk Statement Date</label>
            <div className="flex gap-2">
              <Input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="h-9 w-40" data-testid="input-bulk-stmt-date" />
              <Button variant="outline" size="sm" onClick={applyBulkDate} disabled={!bulkDate} data-testid="btn-apply-bulk-date">Apply to ticked rows</Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {data && totals && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="reconciliation-summary">
            <Stat label="Opening" value={`₹ ${formatINR(data.opening_balance)}`} />
            <Stat label="Book Balance" value={`₹ ${formatINR(totals.book)}`} accent="text-blue-700" />
            <Stat label="Reconciled" value={`₹ ${formatINR(totals.reconciled)}`} accent="text-emerald-700" />
            <Stat label="Unreconciled" value={`₹ ${formatINR(totals.unreconciled)}`} accent={totals.unreconciled !== 0 ? "text-orange-700" : "text-gray-900"} />
            <Stat label="Statement Bal" value={`₹ ${formatINR(totals.statement)}`} />
          </div>
        )}

        {/* Table */}
        {data && (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            <table className="tx-table">
              <thead>
                <tr>
                  <th className="w-10">
                    <Checkbox
                      checked={allVisibleSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                      data-testid="check-all-rows"
                    />
                  </th>
                  <th>Date</th>
                  <th>Voucher</th>
                  <th>Type</th>
                  <th>Particulars</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Credit</th>
                  <th className="text-right">Balance</th>
                  <th>Stmt Date</th>
                  <th>Stmt Ref</th>
                </tr>
              </thead>
              <tbody data-testid="reconciliation-rows">
                {rows.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-xs text-gray-400 py-6">No transactions in this period</td></tr>
                ) : rows.map((r) => (
                  <tr key={r._key} className={r._reconciled ? "bg-emerald-50/40" : ""} data-testid={`row-${r._key}`}>
                    <td>
                      <Checkbox
                        checked={r._reconciled}
                        onCheckedChange={() => toggleRow(r)}
                        data-testid={`check-${r._key}`}
                      />
                    </td>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td className="font-mono text-xs">{r.voucher_no}</td>
                    <td className="text-xs">{r.voucher_type}</td>
                    <td className="text-xs">{r.particulars}</td>
                    <td className="tx-num">{r.dr_cr === "Dr" ? formatINR(r.amount) : ""}</td>
                    <td className="tx-num">{r.dr_cr === "Cr" ? formatINR(r.amount) : ""}</td>
                    <td className="tx-num">{formatINR(r.balance)}</td>
                    <td>
                      <Input
                        type="date"
                        value={r._statement_date || ""}
                        onChange={(e) => updateRow(r._key, { reconciled: !!e.target.value, statement_date: e.target.value, statement_ref: r._statement_ref || "" })}
                        disabled={!r._reconciled}
                        className="h-7 text-xs w-36"
                        data-testid={`input-stmt-date-${r._key}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={r._statement_ref || ""}
                        onChange={(e) => updateRow(r._key, { reconciled: r._reconciled, statement_date: r._statement_date, statement_ref: e.target.value })}
                        disabled={!r._reconciled}
                        placeholder="UTR / Cheque #"
                        className="h-7 text-xs w-32"
                        data-testid={`input-stmt-ref-${r._key}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Help footer */}
        <div className="text-xs text-gray-400 flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Reconciled rows appear with green highlight</span>
          <span className="inline-flex items-center gap-1"><X className="w-3 h-3 text-orange-600" /> &quot;Unreconciled&quot; amount tells you the difference between book and bank statement</span>
        </div>
      </div>
    </>
  );
}

export default function BankReconciliation() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
